import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import { Button } from "design-system/atoms/Button"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import config from "lib/config"
import { features, type Features } from "lib/table"
import { CircleAlert, Trash } from "lucide-react"
import { useInstagramIntegrationsQuery } from "modules/settings/hooks/instagram/useInstagramIntegrationsQuery"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { InstagramIntegrationsRespondeData } from "schema"
import { z } from "zod"
import { DeleteInstagramIntegration } from "./DeleteInstagramIntegration"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof InstagramIntegrationsRespondeData>[number]
>()

export const InstagramIntegrationsTable = () => {
  const { t } = useTranslation()
  const { data, isPending } = useInstagramIntegrationsQuery()
  const [integrationToDelete, setIntegrationToDelete] = useState<
    z.infer<typeof InstagramIntegrationsRespondeData>[number] | null
  >(null)
  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("profilePicture", {
          header: t("settings.integrations.instagram.table.profilePicture"),
          cell: (props) => {
            const image = props.getValue()

            return (
              <img
                src={image ?? "/insta-no-profile.png"}
                className="object-contain rounded-full"
                width={40}
                height={40}
              />
            )
          },
          meta: { colSpan: 3 },
        }),
        columnHelper.accessor("username", {
          header: t("settings.integrations.instagram.table.username"),
          meta: { colSpan: 3 },
          cell: (props) => {
            const hasError = props.row.original.failedAt

            return (
              <div className="flex gap-2 items-center">
                {hasError && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <CircleAlert className="text-destructive" size={16} />
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("settings.integrations.instagram.table.failedTooltip")}
                    </TooltipContent>
                  </Tooltip>
                )}
                <span>{props.getValue()}</span>
              </div>
            )
          },
        }),
        columnHelper.accessor("name", {
          header: t("settings.integrations.instagram.table.name"),
          meta: { colSpan: 3 },
        }),
        columnHelper.display({
          header: "",
          id: "actions",
          meta: { align: "right" },
          cell: (props) => {
            return (
              <Button
                type="button"
                size={"icon"}
                variant={"ghost"}
                onClick={() => setIntegrationToDelete(props.row.original)}
              >
                <Trash />
              </Button>
            )
          },
        }),
      ]),
    [t],
  )

  const table = useTable({
    data: data ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || data === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (data.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("settings.integrations.instagram.table.noIntegrations")}
          cta={{
            label: t("settings.integrations.instagram.table.addIntegration"),
            href: config.instagramLoginUrl,
          }}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.original.id}>
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            className={cn(cell.column.columnDef.meta?.className)}
            align={cell.column.columnDef.meta?.align}
            {...cell.column.columnDef.meta}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} {...header.column.columnDef.meta}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="**:data-[slot=table-cell]:first:w-8">{renderContent()}</TableBody>
      </Table>
      <DeleteInstagramIntegration
        integration={integrationToDelete}
        onOpenChange={(isOpen) => {
          if (!isOpen) setIntegrationToDelete(null)
        }}
      />
    </>
  )
}
